import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Check, RotateCcw, Send, Loader2, ArrowLeft, Eye } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { CSEN_COUNTRIES } from "@/data/countries";
import { ITALIAN_PROVINCES } from "@/data/italianCities";
import CityCombobox from "@/components/CityCombobox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function DirectMemberPage() {
  const [step, setStep] = useState(1);
  const [lang, setLang] = useState("it");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showLangMenu, setShowLangMenu] = useState(false);

  // Auto-detect language from IP
  useEffect(() => {
    fetch("https://ipapi.co/json/").then(r => r.json()).then(data => {
      const country = (data.country_code || "").toUpperCase();
      const langMap = { DE: "de", AT: "de", CH: "de", GB: "en", US: "en", AU: "en", IE: "en", FR: "fr", BE: "fr", MC: "fr", ES: "es", AR: "es", MX: "es", CO: "es", CL: "es" };
      if (langMap[country]) setLang(langMap[country]);
    }).catch(() => {});
  }, []);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    sex: "",
    birth_date: "",
    birth_place: "",
    birth_province: "",
    fiscal_code: "",
    address: "",
    cap: "",
    province: "",
    city: "",
    nationality: "Italiana",
    email: "",
    phone: "",
    age: "",
    weight: "",
    // Minor / parent
    is_minor: false,
    parent_name: "",
    parent_surname: "",
    parent_birth_date: "",
    parent_birth_place: "",
    parent_birth_province: "",
    parent_sex: "",
    parent_fiscal_code: "",
    parent_address: "",
    parent_cap: "",
    parent_province: "",
    parent_city: "",
    parent_nationality: "Italiana",
    parent_email: "",
    parent_phone: "",
    // Declarations
    accepts_statute: false,
    accepts_medical_responsibility: false,
    accepts_medical_delivery: false,
    accepts_data_veracity: false,
    // Privacy GDPR
    privacy_association_consent: false,
    privacy_csen_consent: false,
    marketing_association_consent: false,
    marketing_csen_consent: false,
    // Signature
    signature_data: "",
  });

  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  const t = {
    it: {
      title: "MODULO DI ADESIONE AL CIRCOLO",
      subtitle: "PORTOPOLLO ACADEMY",
      fee: "Tesseramento CSEN - Quota: 10,00 EUR",
      steps: ["Dati", "Dichiarazioni", "Anteprima", "Firma"],
      personalData: "IL SOTTOSCRITTO",
      surname: "Cognome", name: "Nome", sex: "Sesso", birthDate: "Data di nascita",
      birthPlace: "Luogo di nascita", birthProv: "Prov.", fiscalCode: "Codice Fiscale",
      address: "Indirizzo", cap: "CAP", province: "Prov.", city: "Comune",
      nationality: "Nazionalita'", email: "Email", phone: "Tel/Cell",
      age: "Eta'", weight: "Peso (kg)", male: "Maschio", female: "Femmina",
      minorSection: "IN CASO DI MINORE — Dati Genitore/Tutore",
      parentSurname: "Cognome", parentName: "Nome", parentBirthDate: "Data nascita",
      parentBirthPlace: "Luogo nascita", parentProv: "Prov.", parentSex: "Sesso",
      parentFiscalCode: "Codice Fiscale", parentAddress: "Indirizzo",
      parentCap: "CAP", parentProvince: "Prov.", parentCity: "Comune",
      parentEmail: "Email", parentPhone: "Tel/Cell",
      next: "Avanti", back: "Indietro", modify: "Modifica", sign: "Firma",
      declarations: "DICHIARAZIONI",
      decl1: "Dichiaro di aver preso visione dello Statuto e dei Regolamenti dell'associazione, di accettarli integralmente e incondizionatamente, di fare quanto sia nelle mie possibilita' e capacita' per il raggiungimento dei fini istituzionali dell'associazione. Dichiaro di essere informato sulle coperture assicurative previste al rilascio della tessera e che l'adesione e' subordinata all'accettazione della domanda da parte dell'organo statutario previsto dallo statuto.",
      decl2: "Dichiaro sotto la propria responsabilita' di essere in possesso del certificato medico di idoneita' psicofisica alla pratica sportiva che intendo esercitare, sollevando l'associazione sportiva, gli istruttori e le persone incaricate da qualsiasi responsabilita' civile e penale al riguardo.",
      decl3: "Mi impegno a consegnare il certificato medico di idoneita' psicofisica in segreteria.",
      decl4: "Garantisco, sotto la propria responsabilita', la veridicita' delle informazioni fornite nel presente modulo, impegnandomi a comunicare qualsiasi variazione ai dati forniti.",
      privacyTitle: "CONSENSO PRIVACY — GDPR 679/2016",
      privacySubtitle: "Presa visione dell'informativa resa ai sensi dell'Art.13 del R.E. 2016/679",
      privacyComm: "Consenso per comunicazioni attivita' statutaria e regolamentare:",
      privacyAssoc: "dell'Associazione",
      privacyCsen: "del CSEN — Centro Sportivo Educativo Nazionale",
      privacyMkt: "Consenso per materiale pubblicitario/informativo (marketing):",
      preview: "ANTEPRIMA MODULO", previewSubtitle: "Verifica i dati prima di firmare",
      signTitle: "FIRMA", signDesc: "L'iscritto dichiara di aver preso visione del presente modulo di iscrizione, accettando ogni sua parte.",
      signHint: "Usa il dito o il mouse per firmare:",
      clearSig: "Cancella Firma", submit: "Invia Iscrizione", submitting: "Invio...",
      errorRequired: "Compila cognome, nome, email e telefono",
      errorDecl: "Accetta tutte le dichiarazioni obbligatorie",
      errorSig: "Firma il modulo per procedere",
      successTitle: "Iscrizione Completata!",
      successMsg: "La tua iscrizione al Circolo Portopollo Academy e' stata registrata.",
      successFee: "Quota associativa:",
      successToast: "Iscrizione inviata con successo!",
      errorToast: "Errore nell'invio. Riprova.",
      consent: "Consenso", denied: "Non dato",
      previewPersonal: "IL SOTTOSCRITTO:", previewParent: "GENITORE/TUTORE:",
      previewDecl: "DICHIARAZIONI:", previewPrivacy: "PRIVACY GDPR:",
    },
    en: {
      title: "MEMBERSHIP APPLICATION FORM",
      subtitle: "PORTOPOLLO ACADEMY",
      fee: "CSEN Membership - Fee: 10.00 EUR",
      steps: ["Data", "Declarations", "Preview", "Sign"],
      personalData: "THE UNDERSIGNED",
      surname: "Surname", name: "Name", sex: "Sex", birthDate: "Date of birth",
      birthPlace: "Place of birth", birthProv: "Prov.", fiscalCode: "Tax Code",
      address: "Address", cap: "Postal Code", province: "Prov.", city: "City",
      nationality: "Nationality", email: "Email", phone: "Phone",
      age: "Age", weight: "Weight (kg)", male: "Male", female: "Female",
      minorSection: "IF MINOR — Parent/Guardian Details",
      parentSurname: "Surname", parentName: "Name", parentBirthDate: "Date of birth",
      parentBirthPlace: "Place of birth", parentProv: "Prov.", parentSex: "Sex",
      parentFiscalCode: "Tax Code", parentAddress: "Address",
      parentCap: "Postal Code", parentProvince: "Prov.", parentCity: "City",
      parentEmail: "Email", parentPhone: "Phone",
      next: "Next", back: "Back", modify: "Edit", sign: "Sign",
      declarations: "DECLARATIONS",
      decl1: "I declare that I have read the Statute and Regulations of the association, that I accept them fully and unconditionally, and that I will do everything in my power to achieve the institutional goals of the association. I declare that I am aware of the insurance coverage provided upon issuance of the membership card and that membership is subject to acceptance by the statutory body.",
      decl2: "I declare under my own responsibility that I possess a medical certificate of physical and mental fitness for the sport I intend to practice, releasing the sports association, instructors and persons in charge from any civil and criminal liability in this regard.",
      decl3: "I undertake to deliver the medical certificate of fitness to the secretariat.",
      decl4: "I guarantee, under my own responsibility, the truthfulness of the information provided in this form, undertaking to communicate any changes to the data provided.",
      privacyTitle: "PRIVACY CONSENT — GDPR 679/2016",
      privacySubtitle: "Having read the information provided pursuant to Art.13 of EU Reg. 2016/679",
      privacyComm: "Consent for statutory and regulatory activity communications:",
      privacyAssoc: "of the Association",
      privacyCsen: "of CSEN — Centro Sportivo Educativo Nazionale",
      privacyMkt: "Consent for advertising/informational material (marketing):",
      preview: "FORM PREVIEW", previewSubtitle: "Check your data before signing",
      signTitle: "SIGNATURE", signDesc: "The member declares to have read this membership form, accepting all its parts.",
      signHint: "Use your finger or mouse to sign:",
      clearSig: "Clear Signature", submit: "Submit", submitting: "Sending...",
      errorRequired: "Fill in surname, name, email and phone",
      errorDecl: "Accept all mandatory declarations",
      errorSig: "Sign the form to proceed",
      successTitle: "Registration Complete!",
      successMsg: "Your membership to Circolo Portopollo Academy has been registered.",
      successFee: "Membership fee:",
      successToast: "Registration submitted successfully!",
      errorToast: "Error submitting. Please try again.",
      consent: "Consent", denied: "Not given",
      previewPersonal: "THE UNDERSIGNED:", previewParent: "PARENT/GUARDIAN:",
      previewDecl: "DECLARATIONS:", previewPrivacy: "PRIVACY GDPR:",
    },
    de: {
      title: "BEITRITTSFORMULAR ZUM VEREIN",
      subtitle: "PORTOPOLLO ACADEMY",
      fee: "CSEN-Mitgliedschaft - Gebuhr: 10,00 EUR",
      steps: ["Daten", "Erklarungen", "Vorschau", "Unterschrift"],
      personalData: "DER/DIE UNTERZEICHNETE",
      surname: "Nachname", name: "Vorname", sex: "Geschlecht", birthDate: "Geburtsdatum",
      birthPlace: "Geburtsort", birthProv: "Prov.", fiscalCode: "Steuernummer",
      address: "Adresse", cap: "PLZ", province: "Prov.", city: "Stadt",
      nationality: "Staatsangehorigkeit", email: "E-Mail", phone: "Telefon",
      age: "Alter", weight: "Gewicht (kg)", male: "Mannlich", female: "Weiblich",
      minorSection: "BEI MINDERJAHRIGEN — Daten des Erziehungsberechtigten",
      parentSurname: "Nachname", parentName: "Vorname", parentBirthDate: "Geburtsdatum",
      parentBirthPlace: "Geburtsort", parentProv: "Prov.", parentSex: "Geschlecht",
      parentFiscalCode: "Steuernummer", parentAddress: "Adresse",
      parentCap: "PLZ", parentProvince: "Prov.", parentCity: "Stadt",
      parentEmail: "E-Mail", parentPhone: "Telefon",
      next: "Weiter", back: "Zuruck", modify: "Bearbeiten", sign: "Unterschrift",
      declarations: "ERKLARUNGEN",
      decl1: "Ich erklare, dass ich die Satzung und die Vorschriften des Vereins gelesen habe, sie vollstandig und bedingungslos akzeptiere und alles in meiner Macht Stehende tun werde, um die Vereinsziele zu erreichen. Ich erklare, dass ich uber den Versicherungsschutz bei Ausgabe der Mitgliedskarte informiert bin und dass die Mitgliedschaft von der Annahme durch das satzungsgemasse Organ abhangt.",
      decl2: "Ich erklare unter eigener Verantwortung, dass ich im Besitz eines arztlichen Attests uber die korperliche und geistige Eignung fur den Sport bin, den ich ausuben mochte, und stelle den Sportverein, die Trainer und die beauftragten Personen von jeglicher zivil- und strafrechtlicher Haftung frei.",
      decl3: "Ich verpflichte mich, das arztliche Eignungszeugnis im Sekretariat abzugeben.",
      decl4: "Ich garantiere unter eigener Verantwortung die Richtigkeit der in diesem Formular gemachten Angaben und verpflichte mich, Anderungen mitzuteilen.",
      privacyTitle: "DATENSCHUTZ-EINWILLIGUNG — DSGVO 679/2016",
      privacySubtitle: "Nach Kenntnisnahme der Informationen gemas Art.13 der EU-Verordnung 2016/679",
      privacyComm: "Einwilligung fur satzungsgemase Mitteilungen:",
      privacyAssoc: "des Vereins",
      privacyCsen: "des CSEN — Centro Sportivo Educativo Nazionale",
      privacyMkt: "Einwilligung fur Werbe-/Informationsmaterial (Marketing):",
      preview: "FORMULARVORSCHAU", previewSubtitle: "Uberprufen Sie Ihre Daten vor der Unterschrift",
      signTitle: "UNTERSCHRIFT", signDesc: "Das Mitglied erklart, dieses Beitrittsformular gelesen zu haben und alle Teile zu akzeptieren.",
      signHint: "Verwenden Sie Ihren Finger oder die Maus zum Unterschreiben:",
      clearSig: "Unterschrift loschen", submit: "Absenden", submitting: "Wird gesendet...",
      errorRequired: "Fullen Sie Nachname, Vorname, E-Mail und Telefon aus",
      errorDecl: "Akzeptieren Sie alle Pflichterkklarungen",
      errorSig: "Unterschreiben Sie das Formular um fortzufahren",
      successTitle: "Anmeldung abgeschlossen!",
      successMsg: "Ihre Mitgliedschaft beim Circolo Portopollo Academy wurde registriert.",
      successFee: "Mitgliedsbeitrag:",
      successToast: "Anmeldung erfolgreich gesendet!",
      errorToast: "Fehler beim Senden. Bitte versuchen Sie es erneut.",
      consent: "Einwilligung", denied: "Nicht erteilt",
      previewPersonal: "DER/DIE UNTERZEICHNETE:", previewParent: "ERZIEHUNGSBERECHTIGTER:",
      previewDecl: "ERKLARUNGEN:", previewPrivacy: "DATENSCHUTZ DSGVO:",
    },
    fr: {
      title: "FORMULAIRE D'ADHESION AU CLUB",
      subtitle: "PORTOPOLLO ACADEMY",
      fee: "Adhesion CSEN - Cotisation: 10,00 EUR",
      steps: ["Donnees", "Declarations", "Apercu", "Signature"],
      personalData: "LE SOUSSIGNE",
      surname: "Nom", name: "Prenom", sex: "Sexe", birthDate: "Date de naissance",
      birthPlace: "Lieu de naissance", birthProv: "Prov.", fiscalCode: "Code fiscal",
      address: "Adresse", cap: "Code postal", province: "Prov.", city: "Ville",
      nationality: "Nationalite", email: "Email", phone: "Telephone",
      age: "Age", weight: "Poids (kg)", male: "Homme", female: "Femme",
      minorSection: "EN CAS DE MINEUR — Donnees du parent/tuteur",
      parentSurname: "Nom", parentName: "Prenom", parentBirthDate: "Date de naissance",
      parentBirthPlace: "Lieu de naissance", parentProv: "Prov.", parentSex: "Sexe",
      parentFiscalCode: "Code fiscal", parentAddress: "Adresse",
      parentCap: "Code postal", parentProvince: "Prov.", parentCity: "Ville",
      parentEmail: "Email", parentPhone: "Telephone",
      next: "Suivant", back: "Retour", modify: "Modifier", sign: "Signature",
      declarations: "DECLARATIONS",
      decl1: "Je declare avoir pris connaissance des Statuts et Reglements de l'association, les accepter integralement et inconditionnellement, et faire tout ce qui est en mon pouvoir pour atteindre les objectifs institutionnels de l'association. Je declare etre informe des couvertures d'assurance prevues lors de la delivrance de la carte et que l'adhesion est subordonnee a l'acceptation par l'organe statutaire.",
      decl2: "Je declare sous ma responsabilite etre en possession d'un certificat medical d'aptitude physique et mentale pour le sport que j'entends pratiquer, en dechargant l'association sportive, les moniteurs et les personnes chargees de toute responsabilite civile et penale.",
      decl3: "Je m'engage a remettre le certificat medical d'aptitude au secretariat.",
      decl4: "Je garantis, sous ma responsabilite, la veracite des informations fournies dans ce formulaire et m'engage a communiquer toute modification.",
      privacyTitle: "CONSENTEMENT CONFIDENTIALITE — RGPD 679/2016",
      privacySubtitle: "Apres avoir lu les informations fournies au titre de l'Art.13 du RE 2016/679",
      privacyComm: "Consentement pour les communications d'activite statutaire:",
      privacyAssoc: "de l'Association",
      privacyCsen: "du CSEN — Centro Sportivo Educativo Nazionale",
      privacyMkt: "Consentement pour le materiel publicitaire/informatif (marketing):",
      preview: "APERCU DU FORMULAIRE", previewSubtitle: "Verifiez vos donnees avant de signer",
      signTitle: "SIGNATURE", signDesc: "L'adherent declare avoir pris connaissance du present formulaire d'adhesion, en acceptant toutes ses parties.",
      signHint: "Utilisez votre doigt ou la souris pour signer:",
      clearSig: "Effacer la signature", submit: "Envoyer", submitting: "Envoi en cours...",
      errorRequired: "Remplissez nom, prenom, email et telephone",
      errorDecl: "Acceptez toutes les declarations obligatoires",
      errorSig: "Signez le formulaire pour continuer",
      successTitle: "Inscription terminee!",
      successMsg: "Votre adhesion au Circolo Portopollo Academy a ete enregistree.",
      successFee: "Cotisation:",
      successToast: "Inscription envoyee avec succes!",
      errorToast: "Erreur lors de l'envoi. Veuillez reessayer.",
      consent: "Consentement", denied: "Non donne",
      previewPersonal: "LE SOUSSIGNE:", previewParent: "PARENT/TUTEUR:",
      previewDecl: "DECLARATIONS:", previewPrivacy: "CONFIDENTIALITE RGPD:",
    },
    es: {
      title: "FORMULARIO DE ADHESION AL CLUB",
      subtitle: "PORTOPOLLO ACADEMY",
      fee: "Membresia CSEN - Cuota: 10,00 EUR",
      steps: ["Datos", "Declaraciones", "Vista previa", "Firma"],
      personalData: "EL ABAJO FIRMANTE",
      surname: "Apellido", name: "Nombre", sex: "Sexo", birthDate: "Fecha de nacimiento",
      birthPlace: "Lugar de nacimiento", birthProv: "Prov.", fiscalCode: "Codigo fiscal",
      address: "Direccion", cap: "Codigo postal", province: "Prov.", city: "Ciudad",
      nationality: "Nacionalidad", email: "Email", phone: "Telefono",
      age: "Edad", weight: "Peso (kg)", male: "Hombre", female: "Mujer",
      minorSection: "EN CASO DE MENOR — Datos del padre/tutor",
      parentSurname: "Apellido", parentName: "Nombre", parentBirthDate: "Fecha de nacimiento",
      parentBirthPlace: "Lugar de nacimiento", parentProv: "Prov.", parentSex: "Sexo",
      parentFiscalCode: "Codigo fiscal", parentAddress: "Direccion",
      parentCap: "Codigo postal", parentProvince: "Prov.", parentCity: "Ciudad",
      parentEmail: "Email", parentPhone: "Telefono",
      next: "Siguiente", back: "Atras", modify: "Editar", sign: "Firma",
      declarations: "DECLARACIONES",
      decl1: "Declaro haber leido los Estatutos y Reglamentos de la asociacion, aceptarlos integra e incondicionalmente, y hacer todo lo posible para alcanzar los fines institucionales de la asociacion. Declaro estar informado sobre las coberturas de seguro previstas al emitir la tarjeta y que la adhesion esta sujeta a la aceptacion por el organo estatutario.",
      decl2: "Declaro bajo mi responsabilidad estar en posesion de un certificado medico de aptitud fisica y mental para el deporte que pretendo practicar, eximiendo a la asociacion deportiva, instructores y personas encargadas de toda responsabilidad civil y penal.",
      decl3: "Me comprometo a entregar el certificado medico de aptitud en la secretaria.",
      decl4: "Garantizo, bajo mi responsabilidad, la veracidad de la informacion proporcionada en este formulario y me comprometo a comunicar cualquier cambio.",
      privacyTitle: "CONSENTIMIENTO PRIVACIDAD — RGPD 679/2016",
      privacySubtitle: "Habiendo leido la informacion proporcionada segun el Art.13 del RE 2016/679",
      privacyComm: "Consentimiento para comunicaciones de actividad estatutaria:",
      privacyAssoc: "de la Asociacion",
      privacyCsen: "del CSEN — Centro Sportivo Educativo Nazionale",
      privacyMkt: "Consentimiento para material publicitario/informativo (marketing):",
      preview: "VISTA PREVIA DEL FORMULARIO", previewSubtitle: "Verifique sus datos antes de firmar",
      signTitle: "FIRMA", signDesc: "El inscrito declara haber leido el presente formulario de inscripcion, aceptando todas sus partes.",
      signHint: "Use su dedo o el raton para firmar:",
      clearSig: "Borrar firma", submit: "Enviar", submitting: "Enviando...",
      errorRequired: "Complete apellido, nombre, email y telefono",
      errorDecl: "Acepte todas las declaraciones obligatorias",
      errorSig: "Firme el formulario para continuar",
      successTitle: "Inscripcion completada!",
      successMsg: "Su membresia al Circolo Portopollo Academy ha sido registrada.",
      successFee: "Cuota de membresia:",
      successToast: "Inscripcion enviada con exito!",
      errorToast: "Error al enviar. Intentelo de nuevo.",
      consent: "Consentimiento", denied: "No dado",
      previewPersonal: "EL ABAJO FIRMANTE:", previewParent: "PADRE/TUTOR:",
      previewDecl: "DECLARACIONES:", previewPrivacy: "PRIVACIDAD RGPD:",
    }
  }[lang];

  useEffect(() => {
    if (step === 4 && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.strokeStyle = "#000000";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [step]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const getCoordinates = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    if (e.touches) {
      return { x: (e.touches[0].clientX - rect.left) * scaleX, y: (e.touches[0].clientY - rect.top) * scaleY };
    }
    return { x: (e.clientX - rect.left) * scaleX, y: (e.clientY - rect.top) * scaleY };
  };

  const startDrawing = (e) => { e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = getCoordinates(e); ctx.beginPath(); ctx.moveTo(x, y); setIsDrawing(true); setHasSignature(true); };
  const draw = (e) => { if (!isDrawing) return; e.preventDefault(); const ctx = canvasRef.current.getContext("2d"); const { x, y } = getCoordinates(e); ctx.lineTo(x, y); ctx.stroke(); };
  const stopDrawing = () => { if (isDrawing) { canvasRef.current.getContext("2d").closePath(); setIsDrawing(false); setFormData(prev => ({ ...prev, signature_data: canvasRef.current.toDataURL("image/png") })); } };
  const clearSignature = () => { const ctx = canvasRef.current.getContext("2d"); ctx.fillStyle = "#ffffff"; ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height); setHasSignature(false); setFormData(prev => ({ ...prev, signature_data: "" })); };

  const handleSubmit = async () => {
    if (!formData.signature_data) { toast.error(t.errorSig); return; }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/direct-registration`, formData);
      toast.success(t.successToast);
      setSubmitted(true);
    } catch (error) {
      toast.error(t.errorToast);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-teal-50 to-cyan-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl p-8 max-w-md w-full text-center shadow-lg">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Check className="w-8 h-8 text-green-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">{t.successTitle}</h2>
          <p className="text-gray-600 mb-4">{t.successMsg}</p>
          <p className="text-sm text-gray-500 mb-6">{t.successFee} <strong>10,00 EUR</strong></p>
          <div className="text-sm text-gray-500 space-y-1">
            <p>WhatsApp: +39 351 364 4743</p>
            <p>Email: iscrizioni@portopolloacademy.com</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-teal-50 to-cyan-50 flex flex-col" data-testid="direct-member-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-700 text-white p-4 shadow-lg">
        <div className="max-w-lg mx-auto flex justify-between items-center">
          <div>
            <h1 className="text-lg font-bold">{t.title}</h1>
            <p className="text-sm text-white/90">{t.subtitle}</p>
            <p className="text-xs text-white/60 mt-1">{t.fee}</p>
          </div>
          <div className="relative">
            <button onClick={() => setShowLangMenu(!showLangMenu)} className="flex items-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded text-xs text-white">
              {lang.toUpperCase()} <span className="text-white/70 text-[10px]">Change language</span> <ArrowLeft className="w-3 h-3 rotate-[-90deg]" />
            </button>
            {showLangMenu && (
              <div className="absolute right-0 mt-1 bg-white rounded-lg shadow-lg py-1 z-50 min-w-[120px]">
                {[["it","Italiano"],["en","English"],["de","Deutsch"],["fr","Francais"],["es","Espanol"]].map(([code,label]) => (
                  <button key={code} onClick={() => { setLang(code); setShowLangMenu(false); }}
                    className={`block w-full text-left px-3 py-1.5 text-sm ${lang === code ? "bg-teal-50 text-teal-700 font-bold" : "text-gray-700 hover:bg-gray-50"}`}>
                    {label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-lg mx-auto w-full px-4 pt-4">
        <div className="flex gap-1.5">
          {[1, 2, 3, 4].map(s => (
            <div key={s} className="flex-1 h-2 rounded-full overflow-hidden bg-gray-200">
              <div className={`h-full transition-all duration-300 ${s <= step ? "bg-teal-500" : "bg-gray-200"}`} style={{ width: s <= step ? "100%" : "0%" }} />
            </div>
          ))}
        </div>
        <div className="flex justify-between text-[10px] text-gray-500 mt-1">
          {t.steps.map((s, i) => <span key={i}>{s}</span>)}
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 max-w-lg mx-auto w-full p-4 overflow-y-auto">

        {/* STEP 1: Personal Data */}
        {step === 1 && (() => {
          const isItalian = formData.nationality === "Italia" || formData.nationality === "Italiana";
          return (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-3">
            <h3 className="font-semibold text-gray-900">{t.personalData}</h3>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t.surname} *</Label><Input value={formData.surname} onChange={e => handleChange("surname", e.target.value)} placeholder="Rossi" data-testid="surname-input" /></div>
              <div><Label>{t.name} *</Label><Input value={formData.name} onChange={e => handleChange("name", e.target.value)} placeholder="Mario" data-testid="name-input" /></div>
            </div>

            <div>
              <Label>{t.nationality} *</Label>
              <Select value={formData.nationality} onValueChange={v => {
                handleChange("nationality", v);
                handleChange("birth_province", "");
                handleChange("birth_place", "");
                if (v !== "Italia" && v !== "Italiana") {
                  handleChange("fiscal_code", "");
                  handleChange("cap", "");
                  handleChange("province", "");
                  handleChange("city", "");
                }
              }}>
                <SelectTrigger><SelectValue placeholder="Italia" /></SelectTrigger>
                <SelectContent className="max-h-60">
                  {CSEN_COUNTRIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div className={isItalian ? "grid grid-cols-3 gap-3" : ""}>
              <div><Label>{t.birthDate} *</Label><Input type="date" value={formData.birth_date} onChange={e => handleChange("birth_date", e.target.value)} data-testid="birth-date-input" /></div>
              {isItalian && (
                <>
                  <div>
                    <Label>{t.province} *</Label>
                    <Select value={formData.birth_province} onValueChange={v => { handleChange("birth_province", v); handleChange("birth_place", ""); }}>
                      <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                      <SelectContent className="max-h-60">
                        {ITALIAN_PROVINCES.map(p => <SelectItem key={p.code} value={p.code}>{p.code} - {p.name}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>{t.birthPlace} *</Label>
                    {/* Custom combobox — Radix Select crashes Chrome on provinces
                        with 200-300+ cities (TO=317). Native datalist doesn't show
                        a clickable dropdown on mobile. This is a lightweight inline
                        combobox with search + max 60 visible results. */}
                    <CityCombobox
                      value={formData.birth_place}
                      onChange={v => handleChange("birth_place", v)}
                      options={ITALIAN_PROVINCES.find(p => p.code === formData.birth_province)?.cities || []}
                      placeholder="Seleziona citta'..."
                      disabled={!formData.birth_province}
                      disabledPlaceholder="Seleziona prima la provincia"
                      testid="birth-place-input"
                    />
                  </div>
                </>
              )}
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{t.sex}</Label>
                <Select value={formData.sex} onValueChange={v => handleChange("sex", v)}>
                  <SelectTrigger><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent><SelectItem value="M">{t.male}</SelectItem><SelectItem value="F">{t.female}</SelectItem></SelectContent>
                </Select>
              </div>
              {isItalian && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label>{t.fiscalCode}</Label>
                    <a href="https://www.codicefiscaleonline.com/" target="_blank" rel="noopener noreferrer" className="text-[10px] text-teal-600 hover:text-teal-800 underline">
                      Calcola codice fiscale
                    </a>
                  </div>
                  <Input value={formData.fiscal_code} onChange={e => handleChange("fiscal_code", e.target.value.toUpperCase())} placeholder="RSSMRA80A01H501U" />
                </div>
              )}
            </div>

            <div><Label>{t.address}</Label><Input value={formData.address} onChange={e => handleChange("address", e.target.value)} placeholder="Via Roma 1" /></div>

            {isItalian && (
              <div className="grid grid-cols-3 gap-3">
                <div><Label>{t.cap}</Label><Input value={formData.cap} onChange={e => handleChange("cap", e.target.value)} placeholder="07020" /></div>
                <div><Label>{t.province}</Label><Input value={formData.province} onChange={e => handleChange("province", e.target.value)} placeholder="SS" /></div>
                <div><Label>{t.city}</Label><Input value={formData.city} onChange={e => handleChange("city", e.target.value)} placeholder="Palau" /></div>
              </div>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t.email} *</Label><Input type="email" value={formData.email} onChange={e => handleChange("email", e.target.value)} placeholder="mario@email.com" data-testid="email-input" /></div>
              <div><Label>{t.phone} *</Label><Input type="tel" value={formData.phone} onChange={e => handleChange("phone", e.target.value)} placeholder="+39 351..." data-testid="phone-input" /></div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t.age} *</Label><Input type="number" min="4" max="99" value={formData.age} onChange={e => handleChange("age", e.target.value)} placeholder="25" /></div>
              <div><Label>{t.weight}</Label><Input type="number" min="15" max="200" value={formData.weight} onChange={e => handleChange("weight", e.target.value)} placeholder="70" /></div>
            </div>

            {/* Minor section */}
            <div className="border rounded-lg p-3 bg-amber-50/50 mt-4">
              <div className="flex items-center gap-2 mb-2">
                <Checkbox checked={formData.is_minor} onCheckedChange={v => handleChange("is_minor", v)} id="is_minor" />
                <Label htmlFor="is_minor" className="text-sm font-semibold">{t.minorSection}</Label>
              </div>
              {formData.is_minor && (
                <div className="space-y-3 mt-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">{t.surname}</Label><Input value={formData.parent_surname} onChange={e => handleChange("parent_surname", e.target.value)} /></div>
                    <div><Label className="text-xs">{t.name}</Label><Input value={formData.parent_name} onChange={e => handleChange("parent_name", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">{t.parentBirthDate}</Label><Input type="date" value={formData.parent_birth_date} onChange={e => handleChange("parent_birth_date", e.target.value)} /></div>
                    <div><Label className="text-xs">{t.parentBirthPlace}</Label><Input value={formData.parent_birth_place} onChange={e => handleChange("parent_birth_place", e.target.value)} /></div>
                    <div><Label className="text-xs">{t.birthProv}</Label><Input value={formData.parent_birth_province} onChange={e => handleChange("parent_birth_province", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Sesso</Label><Select value={formData.parent_sex} onValueChange={v => handleChange("parent_sex", v)}><SelectTrigger><SelectValue placeholder="—" /></SelectTrigger><SelectContent><SelectItem value="M">{t.male}</SelectItem><SelectItem value="F">{t.female}</SelectItem></SelectContent></Select></div>
                    <div><Label className="text-xs">Codice Fiscale</Label><Input value={formData.parent_fiscal_code} onChange={e => handleChange("parent_fiscal_code", e.target.value.toUpperCase())} /></div>
                  </div>
                  <div><Label className="text-xs">Indirizzo</Label><Input value={formData.parent_address} onChange={e => handleChange("parent_address", e.target.value)} /></div>
                  <div className="grid grid-cols-3 gap-3">
                    <div><Label className="text-xs">CAP</Label><Input value={formData.parent_cap} onChange={e => handleChange("parent_cap", e.target.value)} /></div>
                    <div><Label className="text-xs">{t.birthProv}</Label><Input value={formData.parent_province} onChange={e => handleChange("parent_province", e.target.value)} /></div>
                    <div><Label className="text-xs">Comune</Label><Input value={formData.parent_city} onChange={e => handleChange("parent_city", e.target.value)} /></div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">{t.email}</Label><Input value={formData.parent_email} onChange={e => handleChange("parent_email", e.target.value)} /></div>
                    <div><Label className="text-xs">{t.phone}</Label><Input value={formData.parent_phone} onChange={e => handleChange("parent_phone", e.target.value)} /></div>
                  </div>
                </div>
              )}
            </div>

            <Button onClick={() => {
              if (!formData.surname || !formData.name || !formData.email || !formData.phone) { toast.error(t.errorRequired); return; }
              if (!formData.nationality) { toast.error(lang === 'it' ? "Seleziona il paese" : "Select country"); return; }
              if ((formData.nationality === "Italia" || formData.nationality === "Italiana") && (!formData.birth_province || !formData.birth_place)) {
                toast.error(lang === 'it' ? "Seleziona provincia e citta'" : "Select province and city"); return;
              }
              setStep(2);
            }} className="w-full bg-teal-600 hover:bg-teal-700 text-white mt-4" data-testid="step1-next-btn">{t.next}</Button>
          </motion.div>
          );
        })()}

        {/* STEP 2: Declarations + Privacy */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <h3 className="font-semibold text-gray-900">{t.declarations}</h3>

            <div className="space-y-3">
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-gray-50">
                <Checkbox checked={formData.accepts_statute} onCheckedChange={v => handleChange("accepts_statute", v)} id="statute" className="mt-0.5" />
                <Label htmlFor="statute" className="text-xs leading-relaxed">{t.decl1}</Label>
              </div>
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-gray-50">
                <Checkbox checked={formData.accepts_medical_responsibility} onCheckedChange={v => handleChange("accepts_medical_responsibility", v)} id="medical" className="mt-0.5" />
                <Label htmlFor="medical" className="text-xs leading-relaxed">{t.decl2}</Label>
              </div>
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-gray-50">
                <Checkbox checked={formData.accepts_medical_delivery} onCheckedChange={v => handleChange("accepts_medical_delivery", v)} id="delivery" className="mt-0.5" />
                <Label htmlFor="delivery" className="text-xs leading-relaxed">{t.decl3}</Label>
              </div>
              <div className="flex items-start gap-2 p-3 border rounded-lg bg-gray-50">
                <Checkbox checked={formData.accepts_data_veracity} onCheckedChange={v => handleChange("accepts_data_veracity", v)} id="veracity" className="mt-0.5" />
                <Label htmlFor="veracity" className="text-xs leading-relaxed">{t.decl4}</Label>
              </div>
            </div>

            <div className="border-t pt-4 mt-4">
              <h3 className="font-semibold text-gray-900 text-sm mb-1">{t.privacyTitle}</h3>
              <p className="text-xs text-gray-500 mb-3">{t.privacySubtitle}</p>
              <div className="space-y-3">
                <p className="text-xs text-gray-700 font-medium">{t.privacyComm}</p>
                <div className="flex items-center gap-2 pl-2">
                  <Checkbox checked={formData.privacy_association_consent} onCheckedChange={v => handleChange("privacy_association_consent", v)} id="priv_assoc" />
                  <Label htmlFor="priv_assoc" className="text-xs">{t.privacyAssoc}</Label>
                </div>
                <div className="flex items-center gap-2 pl-2">
                  <Checkbox checked={formData.privacy_csen_consent} onCheckedChange={v => handleChange("privacy_csen_consent", v)} id="priv_csen" />
                  <Label htmlFor="priv_csen" className="text-xs">{t.privacyCsen}</Label>
                </div>
                <p className="text-xs text-gray-700 font-medium mt-3">{t.privacyMkt}</p>
                <div className="flex items-center gap-2 pl-2">
                  <Checkbox checked={formData.marketing_association_consent} onCheckedChange={v => handleChange("marketing_association_consent", v)} id="mkt_assoc" />
                  <Label htmlFor="mkt_assoc" className="text-xs">{t.privacyAssoc}</Label>
                </div>
                <div className="flex items-center gap-2 pl-2">
                  <Checkbox checked={formData.marketing_csen_consent} onCheckedChange={v => handleChange("marketing_csen_consent", v)} id="mkt_csen" />
                  <Label htmlFor="mkt_csen" className="text-xs">{t.privacyCsen}</Label>
                </div>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setStep(1)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" /> {t.back}</Button>
              <Button onClick={() => {
                if (!formData.accepts_statute || !formData.accepts_medical_responsibility || !formData.accepts_medical_delivery || !formData.accepts_data_veracity) {
                  toast.error(t.errorDecl); return;
                }
                setStep(3);
              }} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-testid="step2-next-btn">{t.preview}</Button>
            </div>
          </motion.div>
        )}

        {/* STEP 3: Preview */}
        {step === 3 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-5 h-5 text-teal-600" />
              <h3 className="font-semibold text-gray-900">{t.preview}</h3>
            </div>
            <p className="text-xs text-gray-500">{t.previewSubtitle}</p>

            <div className="bg-white border rounded-lg p-4 space-y-3 text-sm shadow-sm">
              <div className="text-center border-b pb-3">
                <p className="font-bold text-base">{t.title}</p>
                <p className="font-bold text-base">{t.subtitle}</p>
              </div>
              <div>
                <p className="font-semibold text-gray-800 text-xs mb-1">{t.previewPersonal}</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <p><span className="text-gray-500">{t.surname}:</span> <strong>{formData.surname}</strong></p>
                  <p><span className="text-gray-500">{t.name}:</span> <strong>{formData.name}</strong></p>
                  <p><span className="text-gray-500">{t.birthDate}:</span> <strong>{formData.birth_date ? formData.birth_date.split('-').reverse().join('/') : ''}</strong></p>
                  <p><span className="text-gray-500">{t.birthPlace}:</span> <strong>{formData.birth_place} ({formData.birth_province})</strong></p>
                  <p><span className="text-gray-500">{t.sex}:</span> <strong>{formData.sex}</strong></p>
                  <p><span className="text-gray-500">{t.fiscalCode}:</span> <strong>{formData.fiscal_code}</strong></p>
                  <p className="col-span-2"><span className="text-gray-500">{t.address}:</span> <strong>{formData.address}, {formData.cap} {formData.city} ({formData.province})</strong></p>
                  <p><span className="text-gray-500">{t.email}:</span> <strong>{formData.email}</strong></p>
                  <p><span className="text-gray-500">{t.phone}:</span> <strong>{formData.phone}</strong></p>
                  <p><span className="text-gray-500">{t.age}:</span> <strong>{formData.age}</strong></p>
                  <p><span className="text-gray-500">{t.weight}:</span> <strong>{formData.weight} kg</strong></p>
                </div>
              </div>
              {formData.is_minor && (
                <div className="border-t pt-2">
                  <p className="font-semibold text-gray-800 text-xs mb-1">{t.previewParent}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                    <p><span className="text-gray-500">{t.surname}:</span> <strong>{formData.parent_surname}</strong></p>
                    <p><span className="text-gray-500">{t.name}:</span> <strong>{formData.parent_name}</strong></p>
                    <p><span className="text-gray-500">{t.fiscalCode}:</span> <strong>{formData.parent_fiscal_code}</strong></p>
                    <p><span className="text-gray-500">{t.phone}:</span> <strong>{formData.parent_phone}</strong></p>
                  </div>
                </div>
              )}
              <div className="border-t pt-2">
                <p className="font-semibold text-gray-800 text-xs mb-1">{t.previewDecl}</p>
                <ul className="text-xs text-gray-600 space-y-1 list-disc list-inside">
                  <li>{t.privacyAssoc}: <strong>{formData.privacy_association_consent ? t.consent : t.denied}</strong></li>
                  <li>{t.privacyCsen}: <strong>{formData.privacy_csen_consent ? t.consent : t.denied}</strong></li>
                </ul>
              </div>
              <div className="border-t pt-2 text-center">
                <p className="text-xs text-gray-500">{t.fee}</p>
              </div>
            </div>

            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setStep(2)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" /> {t.modify}</Button>
              <Button onClick={() => setStep(4)} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-testid="step3-next-btn">{t.sign}</Button>
            </div>
          </motion.div>
        )}

        {/* STEP 4: Signature */}
        {step === 4 && (
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-4">
            <h3 className="font-semibold text-gray-900">{t.signTitle}</h3>
            <p className="text-xs text-gray-600">{t.signDesc}</p>
            <p className="text-xs text-gray-500">{t.signHint}</p>

            <div className="border-2 border-dashed border-gray-300 rounded-xl overflow-hidden bg-white">
              <canvas ref={canvasRef} width={400} height={200} className="w-full touch-none cursor-crosshair"
                onMouseDown={startDrawing} onMouseMove={draw} onMouseUp={stopDrawing} onMouseLeave={stopDrawing}
                onTouchStart={startDrawing} onTouchMove={draw} onTouchEnd={stopDrawing} data-testid="signature-canvas" />
            </div>

            {hasSignature && (
              <Button variant="outline" size="sm" onClick={clearSignature} className="w-full">
                <RotateCcw className="w-4 h-4 mr-2" /> {t.clearSig}
              </Button>
            )}

            <div className="flex gap-3 mt-4">
              <Button variant="outline" onClick={() => setStep(3)} className="flex-1"><ArrowLeft className="w-4 h-4 mr-1" /> {t.back}</Button>
              <Button onClick={handleSubmit} disabled={isSubmitting || !hasSignature} className="flex-1 bg-teal-600 hover:bg-teal-700 text-white" data-testid="submit-btn">
                {isSubmitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t.submitting}</> : <><Send className="w-4 h-4 mr-2" /> {t.submit}</>}
              </Button>
            </div>
          </motion.div>
        )}
      </div>

      {/* Footer */}
      <div className="text-center py-3 text-xs text-gray-400 border-t bg-white">
        <p>WhatsApp: +39 351 364 4743 | iscrizioni@portopolloacademy.com</p>
      </div>
    </div>
  );
}
